import React from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  useColorModeValue,
  Skeleton,
  Tooltip,
} from '@chakra-ui/react';

interface MonthlyKPIs {
  tbv?: number;
  nb_premium_total?: number;
}

interface AnalyticsData {
  monthly?: MonthlyKPIs;
}

interface BookPremiumWidgetProps {
  analytics?: AnalyticsData | null;
  analyticsEnabled?: boolean;
}

function fmt(v: number | undefined | null): string {
  if (v == null) return '—';
  return `$${Math.round(v).toLocaleString('en-CA')}`;
}

export const BookPremiumWidget: React.FC<BookPremiumWidgetProps> = ({
  analytics,
  analyticsEnabled,
}) => {
  const textColor = useColorModeValue('navy.700', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderCol = useColorModeValue('gray.200', 'whiteAlpha.200');

  const monthly = analytics?.monthly;
  // analyticsEnabled=false → flag off, show dashes (not skeleton)
  // analyticsEnabled=true, monthly absent → still loading, show skeleton
  const isLoading = analyticsEnabled && !monthly;
  const hasData = analyticsEnabled && !!monthly;

  const bookSize = hasData ? fmt(monthly?.tbv) : '—';
  const nbPremium = hasData ? fmt(monthly?.nb_premium_total) : '—';

  const card = {
    bg: cardBg,
    borderRadius: '12px',
    border: '1px solid',
    borderColor: borderCol,
    p: '14px 16px',
  };

  const StatCard = ({
    label,
    value,
    sub,
  }: {
    label: string;
    value: string;
    sub?: string;
  }) => (
    <Box
      {...card}
      h="100%"
      minW="0"
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
    >
      <Text
        fontSize={{ base: '10px', hxl: '12px' }}
        fontWeight="600"
        color={mutedColor}
        textTransform="uppercase"
        letterSpacing="0.06em"
        whiteSpace="nowrap"
        mb="8px"
      >
        {label}
      </Text>
      {isLoading ? (
        <Skeleton h="24px" w="60px" borderRadius="4px" />
      ) : (
        <>
          <Text
            fontSize="12px"
            fontWeight="800"
            color={textColor}
            lineHeight="1"
            letterSpacing="-0.03em"
            minW="0"
            w="100%"
            whiteSpace="nowrap"
            title={value}
          >
            {value}
          </Text>
          {sub && (
            <Text fontSize="10px" color={mutedColor} mt="4px">
              {sub}
            </Text>
          )}
        </>
      )}
    </Box>
  );

  const InlineKPI = ({ label, value }: { label: string; value: string }) => (
    <Flex justify="space-between" align="baseline" gap="4px">
      <Text
        fontSize={{ base: '11px', hxl: '12px' }}
        fontWeight="600"
        color={mutedColor}
        whiteSpace="nowrap"
        flexShrink={0}
      >
        {label}
      </Text>
      {isLoading ? (
        <Skeleton h="14px" w="40px" borderRadius="3px" />
      ) : (
        <Text
          fontSize={{ base: '12px', hxl: '13px' }}
          fontWeight="700"
          color={textColor}
          flexShrink={0}
        >
          {value}
        </Text>
      )}
    </Flex>
  );

  const thisMonthBox = (
    <Box {...card} flex="1" minW="0">
      <Text
        fontSize={{ base: '11px', hxl: '12px' }}
        fontWeight="600"
        color={mutedColor}
        textTransform="uppercase"
        letterSpacing="0.06em"
        mb="10px"
      >
        This Month
      </Text>
      <VStack spacing="6px" align="stretch">
        <InlineKPI label="New Business" value={nbPremium} />
        <Tooltip label="Coming Soon" hasArrow placement="top">
          <Flex
            justify="space-between"
            align="baseline"
            gap="4px"
            opacity={0.4}
            cursor="default"
          >
            <Text
              fontSize={{ base: '11px', hxl: '12px' }}
              fontWeight="600"
              color={mutedColor}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              Cancellations
            </Text>
            <Text
              fontSize={{ base: '12px', hxl: '13px' }}
              fontWeight="700"
              color={mutedColor}
              flexShrink={0}
            >
              —
            </Text>
          </Flex>
        </Tooltip>
        <Tooltip label="Coming Soon" hasArrow placement="top">
          <Flex
            justify="space-between"
            align="baseline"
            gap="4px"
            opacity={0.4}
            cursor="default"
          >
            <Text
              fontSize={{ base: '11px', hxl: '12px' }}
              fontWeight="600"
              color={mutedColor}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              Net Growth
            </Text>
            <Text
              fontSize={{ base: '12px', hxl: '13px' }}
              fontWeight="700"
              color={mutedColor}
              flexShrink={0}
            >
              —
            </Text>
          </Flex>
        </Tooltip>
      </VStack>
    </Box>
  );

  return (
    <>
      {/* Small (<hxl): Book Size | Retention | This Month — single row */}
      <Flex
        gap="8px"
        flex="1"
        h="100%"
        w="100%"
        display={{ base: 'flex', hxl: 'none' }}
      >
        <Box flex="1 1 0" minW="0" display="flex" flexDirection="column">
          <StatCard label="Book Size" value={bookSize} />
        </Box>
        <Box flex="1 1 0" minW="0" display="flex" flexDirection="column">
          <Tooltip label="Coming Soon" hasArrow placement="top">
            <Box
              opacity={0.4}
              cursor="default"
              flex="1"
              display="flex"
              flexDirection="column"
            >
              <StatCard label="Retention" value="—" />
            </Box>
          </Tooltip>
        </Box>
        {thisMonthBox}
      </Flex>

      {/* Large (hxl+): Book Size | Retention on top, This Month below */}
      <VStack
        spacing="8px"
        align="stretch"
        flex="1"
        h="100%"
        w="100%"
        display={{ base: 'none', hxl: 'flex' }}
      >
        <Flex gap="8px" flex="1">
          <Box flex="1 1 0" minW="0" display="flex" flexDirection="column">
            <StatCard label="Book Size" value={bookSize} />
          </Box>
          <Box flex="1 1 0" minW="0" display="flex" flexDirection="column">
            <Tooltip label="Coming Soon" hasArrow placement="top">
              <Box
                opacity={0.4}
                cursor="default"
                flex="1"
                display="flex"
                flexDirection="column"
              >
                <StatCard label="Retention" value="—" />
              </Box>
            </Tooltip>
          </Box>
        </Flex>
        {thisMonthBox}
      </VStack>
    </>
  );
};
